import { ThemedHevyProLogo } from 'components/svg/ThemedHevyProLogo';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import React, { CSSProperties } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { PrimaryButton, SecondaryButton } from 'styleguide/Buttons';
import { Modal } from 'styleguide/Modal';
import { H3, P2 } from 'styleguide/Texts';
import { themeState } from 'styleguide/theme';
import { View } from 'styleguide/View';
import { t } from 'translations/t';
import { sendEvent } from 'utils/events';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 400px;
  border-radius: 10px;
`;

const UpperContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const LowerContainer = styled.div`
  margin-top: 10px;
`;

export type PaywallModalType =
  | 'routine-limit'
  | 'history-limit'
  | 'exercise-limit'
  | 'no-api-access';

export interface PaywallModalProps {
  type: PaywallModalType;
  isOpen: boolean;
  onClose: () => void;
  onCancel: () => void;
}

export const PaywallModal = observer(({ type, isOpen, onClose, onCancel }: PaywallModalProps) => {
  const router = useRouter();

  const textMap: { [key in PaywallModalType]: { title: string; subtitle: string } } = {
    'routine-limit': {
      title: t('web.paywallModal.routineLimit'),
      subtitle: t('web.paywallModal.routineLimit.subtitle'),
    },
    'history-limit': {
      title: t('web.paywallModal.historyLimit'),
      subtitle: t('web.paywallModal.historyLimit.subtitle'),
    },
    'exercise-limit': {
      title: t('web.paywallModal.exerciseLimit'),
      subtitle: t('web.paywallModal.exerciseLimit.subtitle'),
    },
    'no-api-access': {
      title: t('web.paywallModal.noApiAccess'),
      subtitle: t('web.paywallModal.noApiAccess.subtitle'),
    },
  };

  return (
    <ThemeProvider theme={themeState.current}>
      <Modal isOpen={isOpen} onClose={onCancel}>
        <Container>
          <UpperContainer>
            <View style={{ padding: 20 }}>
              <ThemedHevyProLogo />
            </View>
            <H3 style={{ marginBottom: 20 }}>{textMap[type].title}</H3>
            <P2>{textMap[type].subtitle}</P2>
          </UpperContainer>
          <LowerContainer>
            <PrimaryButton
              style={styles.button}
              title={t('global.continue')}
              onClick={() => {
                sendEvent('paywall_modal_continue_clicked', { type });
                onClose();
                router.push(`/plans?continue=${router.asPath}`);
              }}
            />
            <SecondaryButton style={styles.button} title={t('global.notNow')} onClick={onCancel} />
          </LowerContainer>
        </Container>
      </Modal>
    </ThemeProvider>
  );
});

const styles: { [key: string]: CSSProperties } = {
  button: {
    width: '100%',
    marginTop: '10px',
  },
};
